// Функция для получения API URL
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) {
    return envUrl;
  }
  const mode = import.meta.env.MODE || import.meta.env.NODE_ENV || 'development';
  const isDev = mode === 'development' || import.meta.env.DEV === true;
  return isDev ? 'http://localhost:3001/api' : '/api';
};

// Используем переменную окружения или fallback
export const API_BASE_URL = getApiBaseUrl();

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    VERIFY_TOKEN: '/auth/verify'
  },
  FEDERATIONS: {
    BASE: '/federations',
    GET: (id: number) => `/federations/${id}`,
    CREATE: '/federations',
    UPDATE: (id: number) => `/federations/${id}`,
    DELETE: (id: number) => `/federations/${id}`
  },
    CLUBS: {
    BASE: '/clubs',
    GET: (id: number) => `/clubs/${id}`,
    CREATE: '/clubs',
    UPDATE: (id: number) => `/clubs/${id}`,
    DELETE: (id: number) => `/clubs/${id}`,
    PARTICIPANTS: {
      BASE: (clubId: number) => {
        return `/clubs/${clubId}/participants`;
      },
      REMOVE: (participantId: number) => `/clubs/participants/${participantId}`,
      ADD: (clubId: number) => `/clubs/${clubId}/participants`
    },
    COACHES: {
      BASE: (clubId: number) => `/clubs/${clubId}/coaches`,
      REMOVE: (coachId: number) => `/clubs/coaches/${coachId}`
    }
  },
  MANAGERS: {
    BASE: '/managers',
    GET: (id: number) => `/managers/${id}`,
    CREATE: '/managers',
    UPDATE: (id: number) => `/managers/${id}`,
    DELETE: (id: number) => `/managers/${id}`
  },
  USERS: {
    BASE: '/users',
    PARTICIPANTS: '/users/participants',
    MANAGERS: '/users/managers',
    MANAGER_CLUB: (id: number) => {
      return `/users/managers/${id}/club`;
    },
    ME: '/users/me'
  },
  PARTICIPANTS: {
    BASE: '/participants',
    SEARCH: '/participants/search',
    GET: (id: number) => `/participants/${id}`,
    CREATE: '/participants',
    UPDATE: (id: number) => `/participants/${id}`,
    DELETE: (id: number) => `/participants/${id}`,
    STATISTICS: (id: number) => `/participants/${id}/statistics`,
    BULK_UPLOAD_TEMPLATE: '/participants/bulk-upload/template',
    BULK_UPLOAD_EXCEL: '/participants/bulk-upload/excel'
  },
  TOURNAMENTS: {
    BASE: '/tournaments',
    CATEGORIES: (id: number) => `/tournaments/${id}?include=categories`,
    GET: (id: number) => `/tournaments/${id}`,
    CREATE: '/tournaments',
    UPDATE: (id: number) => `/tournaments/${id}`,
    STREAM_LINKS: (id: number) => `/tournaments/${id}/stream-links`,
    DELETE: (id: number) => `/tournaments/${id}`,
    BRACKET: (id: number) => `/tournaments/${id}/bracket`,
    REGISTER: (id: number) => `/participants/register/${id}`,
    REGISTER_BULK: (id: number) => `/tournaments/${id}/participants`,
    GET_PARTICIPANTS: (id: number) => `/tournaments/${id}/participants`,
    REGISTERED_PARTICIPANTS: (id: number) => `/tournaments/${id}/registered-participants`,
    REGISTERED_CLUBS: (id: number) => `/tournaments/${id}/clubs`,
    CATEGORY_PARTICIPANTS: (tournamentId: number, categoryId: number) => `/tournaments/${tournamentId}/categories/${categoryId}/participants`,
    SYNC_PARTICIPANT_CATEGORIES: (tournamentId: number) => `/tournaments/${tournamentId}/sync-participant-categories`,
    UPDATE_PARTICIPANT_CATEGORY: (tournamentId: number, participantId: number) => `/tournaments/${tournamentId}/participants/${participantId}/category`,
    REGIONS: '/tournaments/regions',
  },
  CATEGORIES: {
    BASE: '/categories',
    GET: (id: number) => `/categories/${id}`,
    CREATE: '/categories',
    UPDATE: (id: number) => `/categories/${id}`,
    DELETE: (id: number) => `/categories/${id}`
  },
  COACHES: {
    BASE: '/coaches',
    GET: (id: number) => `/coaches/${id}`,
    CREATE: '/coaches',
    UPDATE: (id: number) => `/coaches/${id}`,
    DELETE: (id: number) => `/coaches/${id}`,
    BLOCK: (id: number) => `/coaches/${id}/block`,
    UNBLOCK: (id: number) => `/coaches/${id}/unblock`
  },
  TILES: {
    BASE: '/tiles',
  },
  CLUB_MANAGERS: {
    BASE: '/club-managers',
    BY_CLUB: (clubId: number) => `/club-managers?clubId=${clubId}`,
    GET: (id: number) => `/club-managers/${id}`,
    CREATE: '/club-managers',
    UPDATE: (id: number) => `/club-managers/${id}`,
    DELETE: (id: number) => `/club-managers/${id}`,
    CLUB: (id: number) => `/club-managers/${id}/club`,
    INVITATIONS: '/club-managers/invitations',
    INVITATIONS_BY_CLUB: (clubId: number) => `/club-managers/invitations?clubId=${clubId}`,
    CANCEL_INVITATION: (id: number, clubId: number) => `/club-managers/invitations/${id}?clubId=${clubId}`,
    PENDING: (clubId: number) => `/club-managers/pending?clubId=${clubId}`,
    APPROVE_PENDING: (userId: number, clubId: number) => `/club-managers/approve-pending/${userId}?clubId=${clubId}`,
  },
  MANAGER_INVITATIONS: {
    ACCEPT_INFO: (token: string) => `/manager-invitations/accept?token=${token}`,
    ACCEPT: '/manager-invitations/accept',
  },
  PAYMENTS: {
    BASE: '/payments',
    EXTENDED_LICENSE_INFO: '/payments/extended-license-info',
    SYNC_EXTENDED_LICENSE: '/payments/sync-extended-license',
    EXTENDED_LICENSE: '/payments/extended-license',
    STATUS: (paymentId: string) => `/payments/${paymentId}/status`,
    CLUB: (clubId: number) => `/payments/club/${clubId}`
  },
  SUBSCRIPTIONS: {
    BASE: '/subscriptions',
    GET: (id: number) => `/subscriptions/${id}`,
    CREATE: '/subscriptions',
    UPDATE: (id: number) => `/subscriptions/${id}`,
    DELETE: (id: number) => `/subscriptions/${id}`,
    STATISTICS: '/subscriptions/statistics',
    MY_CLUB: '/subscriptions/my-club',
    PURCHASE: '/subscriptions/purchase',
    SYNC_AFTER_PAYMENT: '/subscriptions/sync-after-payment'
  },
  SUBSCRIPTION_PLANS: {
    BASE: '/subscription-plans',
    ACTIVE: '/subscription-plans/active',
    GET: (id: number) => `/subscription-plans/${id}`,
    CREATE: '/subscription-plans',
    UPDATE: (id: number) => `/subscription-plans/${id}`,
    DELETE: (id: number) => `/subscription-plans/${id}`
  },
  PROMO_CODES: {
    BASE: '/promo-codes',
    GET: (id: number) => `/promo-codes/${id}`,
    CREATE: '/promo-codes',
    UPDATE: (id: number) => `/promo-codes/${id}`,
    DELETE: (id: number) => `/promo-codes/${id}`
  },
  SYSTEM_PARAMETERS: {
    BASE: '/system-parameters',
    GET: (name: string) => `/system-parameters/${name}`,
    SET: (name: string) => `/system-parameters/${name}`,
    DELETE: (name: string) => `/system-parameters/${name}`,
    SUBSCRIPTIONS_ENABLED: '/system-parameters/subscriptions/enabled',
    ENGLISH_LANGUAGE_ENABLED: '/system-parameters/language/enabled'
  },
  INFO_BLOCKS: {
    BASE: '/info-blocks',
    GET: (id: number) => `/info-blocks/${id}`,
    CREATE: '/info-blocks',
    UPDATE: (id: number) => `/info-blocks/${id}`,
    DELETE: (id: number) => `/info-blocks/${id}`
  },
  FEEDBACK: {
    BASE: '/feedback',
    SUBMIT: '/feedback'
  },
  TOURNAMENT_INVITATIONS: {
    BASE: '/tournament-invitations',
    TOURNAMENT: (tournamentId: number) => `/tournament-invitations/tournament/${tournamentId}`,
    INVITE_CLUB: (tournamentId: number) => `/tournament-invitations/tournament/${tournamentId}`,
    INVITE_CLUBS_BULK: (tournamentId: number) => `/tournament-invitations/tournament/${tournamentId}/bulk`,
    ACCEPT: (invitationId: number) => `/tournament-invitations/${invitationId}/accept`,
    REJECT: (invitationId: number) => `/tournament-invitations/${invitationId}/reject`,
    DELETE: (invitationId: number) => `/tournament-invitations/${invitationId}`,
    MY_CLUB: '/tournament-invitations/my-club'
  },
  NOTIFICATIONS: {
    BASE: '/notifications',
    GET: (id: number) => `/notifications/${id}`,
    MARK_READ: (id: number) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    MY_NOTIFICATIONS: '/notifications/my-notifications',
    UNREAD_COUNT: '/notifications/unread-count'
  },
  DOCS: {
    BASE: '/docs',
    LIST: '/docs',
    PAGE: (sectionSlug: string, pageSlug: string) => `/docs/${sectionSlug}/${pageSlug}`,
    UPLOAD_IMAGE: '/docs/upload-image',
    ADMIN_SECTIONS: '/docs/admin/sections',
    ADMIN_SECTION: (id: number) => `/docs/admin/sections/${id}`,
    ADMIN_PAGES: '/docs/admin/pages',
    ADMIN_PAGE: (id: number) => `/docs/admin/pages/${id}`
  }
} as const;

export type ApiEndpoints = typeof API_ENDPOINTS;

export const FILE_CONFIG = {
  UPLOAD: {
    MAX_SIZE: 10 * 1024 * 1024, // 10MB
    MIME_TYPES: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ],
    EXTENSIONS: ['.pdf', '.docx'],
    ERROR_MESSAGES: {
      SIZE: 'Файл слишком большой',
      TYPE: 'Неподдерживаемый тип файла'
    }
  }
} as const;
